/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { subscriptionApi } from '../../services/subscriptionApi';

// Types
export interface Duration {
  duration_key: string;
  label: string;
  days: number;
  discount_percent: number;
  badge?: string;
}

export interface SubscriptionTier {
  id: string;
  name: string;
  price: number;
  meal_frequency?: 'full' | 'light';
  meals_per_day: number;
  features: string[];
  description: string;
  durations?: Duration[];
}

export interface Subscription {
  id: number;
  tier: string;
  status: string;
  meal_frequency?: 'full' | 'light';
  meals_per_day?: number;
  light_meal_slot?: string | null;
  delivery_address_id: number;
  delivery_slot: string;
  start_date: string;
  current_cycle_start: string;
  current_cycle_end: string;
  next_billing_date: string;
  paused_until: string | null;
  pause_reason: string | null;
  cancel_reason: string | null;
  promo_code: string | null;
  discount_amount: number;
  amount: number;
  // Whole-term price and what was actually paid (amount above is per month).
  plan_total?: number;
  total_paid?: number;
  term_days?: number | null;
  is_trial?: boolean;
  created_at: string;
  updated_at: string;
}

export interface PromoValidation {
  valid: boolean;
  discount_type: string | null;
  discount_value: number | null;
  discount_amount: number | null;
  message: string;
}

interface SubscriptionState {
  tiers: SubscriptionTier[];
  activeSub: Subscription | null;
  loading: boolean;
  error: string | null;
  promoValidation: PromoValidation | null;
}

const initialState: SubscriptionState = {
  tiers: [],
  activeSub: null,
  loading: false,
  error: null,
  promoValidation: null,
};

// Async thunks
export const fetchTiers = createAsyncThunk(
  'subscription/fetchTiers',
  async (mealFrequency: 'full' | 'light' = 'full', { rejectWithValue }) => {
    try {
      const response = await subscriptionApi.getTiers(mealFrequency);
      return response.data.tiers;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch subscription tiers');
    }
  }
);

export const createSubscription = createAsyncThunk(
  'subscription/create',
  async (data: { tier: string; delivery_address_id: number; delivery_slot?: string; duration?: string; meal_frequency?: string; light_meal_slot?: string; promo_code?: string }, { rejectWithValue }) => {
    try {
      const response = await subscriptionApi.create(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to create subscription');
    }
  }
);

export const fetchActiveSubscription = createAsyncThunk(
  'subscription/fetchActive',
  async (_, { rejectWithValue }) => {
    try {
      const response = await subscriptionApi.getActive();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch active subscription');
    }
  }
);

export const fetchPausePreview = createAsyncThunk(
  'subscription/fetchPausePreview',
  async ({ id, data }: { id: number; data: { pause_until: string; reason?: string } }, { rejectWithValue }) => {
    try {
      const response = await subscriptionApi.pausePreview(id, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch pause preview');
    }
  }
);

export const pauseSubscriptionFinancial = createAsyncThunk(
  'subscription/pauseFinancial',
  async ({ id, data }: { id: number; data: { pause_until: string; reason?: string } }, { rejectWithValue }) => {
    try {
      const response = await subscriptionApi.pauseFinancial(id, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to pause subscription');
    }
  }
);

export const pauseSubscription = createAsyncThunk(
  'subscription/pause',
  async ({ id, data }: { id: number; data: { pause_until: string; reason?: string } }, { rejectWithValue }) => {
    try {
      const response = await subscriptionApi.pause(id, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to pause subscription');
    }
  }
);

export const resumeSubscription = createAsyncThunk(
  'subscription/resume',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await subscriptionApi.resume(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to resume subscription');
    }
  }
);

export const resumeSubscriptionEarly = createAsyncThunk(
  'subscription/resumeEarly',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await subscriptionApi.resumeEarly(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to resume subscription');
    }
  }
);

export const getPauseStatusData = createAsyncThunk(
  'subscription/getPauseStatus',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await subscriptionApi.getPauseStatus(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch pause status');
    }
  }
);

export const cancelSubscription = createAsyncThunk(
  'subscription/cancel',
  async ({ id, data }: { id: number; data: { reason?: string; cancel_at_cycle_end?: boolean } }, { rejectWithValue }) => {
    try {
      const response = await subscriptionApi.cancel(id, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to cancel subscription');
    }
  }
);

export const validatePromo = createAsyncThunk(
  'subscription/validatePromo',
  async (data: { code: string; tier: string }, { rejectWithValue }) => {
    try {
      const response = await subscriptionApi.validatePromo(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Invalid promo code');
    }
  }
);

// Slice
const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearPromoValidation: (state) => {
      state.promoValidation = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch tiers
    builder.addCase(fetchTiers.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchTiers.fulfilled, (state, action) => {
      state.loading = false;
      state.tiers = action.payload;
    });
    builder.addCase(fetchTiers.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Create subscription
    builder.addCase(createSubscription.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createSubscription.fulfilled, (state, action) => {
      state.loading = false;
      state.activeSub = action.payload;
    });
    builder.addCase(createSubscription.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch active subscription
    builder.addCase(fetchActiveSubscription.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchActiveSubscription.fulfilled, (state, action) => {
      state.loading = false;
      state.activeSub = action.payload;
    });
    builder.addCase(fetchActiveSubscription.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch pause preview
    builder.addCase(fetchPausePreview.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchPausePreview.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(fetchPausePreview.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Pause subscription (financial)
    builder.addCase(pauseSubscriptionFinancial.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(pauseSubscriptionFinancial.fulfilled, (state, action) => {
      state.loading = false;
      if (state.activeSub) {
        // Merge only the changed fields — replacing activeSub with the pause
        // response would clobber paused_until (the response uses pause_until)
        // and drop fields like next_billing_date, causing "Invalid Date" errors.
        state.activeSub = {
          ...state.activeSub,
          status: 'paused',
          paused_until: action.payload.pause_until ?? null,
        };
      }
    });
    builder.addCase(pauseSubscriptionFinancial.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Pause subscription
    builder.addCase(pauseSubscription.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(pauseSubscription.fulfilled, (state, action) => {
      state.loading = false;
      state.activeSub = action.payload;
    });
    builder.addCase(pauseSubscription.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Resume subscription (early)
    builder.addCase(resumeSubscriptionEarly.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(resumeSubscriptionEarly.fulfilled, (state) => {
      state.loading = false;
      if (state.activeSub) {
        state.activeSub = {
          ...state.activeSub,
          status: 'active',
          paused_until: null,
        };
      }
    });
    builder.addCase(resumeSubscriptionEarly.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Get pause status
    builder.addCase(getPauseStatusData.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getPauseStatusData.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(getPauseStatusData.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Resume subscription
    builder.addCase(resumeSubscription.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(resumeSubscription.fulfilled, (state) => {
      state.loading = false;
      if (state.activeSub) {
        state.activeSub = {
          ...state.activeSub,
          status: 'active',
          paused_until: null,
        };
      }
    });
    builder.addCase(resumeSubscription.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Cancel subscription
    builder.addCase(cancelSubscription.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(cancelSubscription.fulfilled, (state, action) => {
      state.loading = false;
      state.activeSub = action.payload;
    });
    builder.addCase(cancelSubscription.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Validate promo
    builder.addCase(validatePromo.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.promoValidation = null;
    });
    builder.addCase(validatePromo.fulfilled, (state, action) => {
      state.loading = false;
      state.promoValidation = action.payload;
    });
    builder.addCase(validatePromo.rejected, (state, action) => {
      state.loading = false;
      state.promoValidation = {
        valid: false,
        discount_type: null,
        discount_value: null,
        discount_amount: null,
        message: action.payload as string,
      };
    });
  },
});

export const { clearError, clearPromoValidation } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
